/**
 * Os números da faixa que abre a lista de Projetos — tudo derivado de `projetos` e `tarefas` já
 * carregados pela tela, sem consulta extra. Reaproveita `calcularProgresso` pra "abertas" e
 * "atrasadas" em vez de reimplementar a regra de tarefa descartada/concluída: o KPI do portfólio
 * fica definicionalmente igual à barra de cada projeto, só que somando tudo.
 */
import type { Projeto } from '../../../types';
import { calcularProgresso, type TarefaProgresso } from './progresso.ts';

export interface ResumoExecucao {
  projetosEmExecucao: number;
  tarefasAbertas: number;
  tarefasAtrasadas: number;
  concluidasUltimos7Dias: number;
  /** Projetos sem NENHUMA tarefa vinculada ainda (mãe ou subtarefa). Inclui projeto cuja única
   *  tarefa foi rejeitada/cancelada — a pergunta é "existe vínculo", não "existe trabalho válido". */
  projetosSemTarefa: Pick<Projeto, 'id' | 'nome'>[];
}

export type TarefaResumoExecucao = TarefaProgresso & {
  // Opcional, não `projeto_id: number | null` — Tarefa.projeto_id é opcional em types.ts, e o
  // Pick<Tarefa, ...> que ProjetosView.tsx usa preserva essa opcionalidade. Se este campo virasse
  // obrigatório aqui, passar o `tarefas` da view direto pra calcularResumoExecucao quebraria o
  // tsc ("Property 'projeto_id' is optional in type '...' but required in type '...'").
  projeto_id?: number | null;
  data_conclusao?: string | null;
};

const DIAS_JANELA_RECENTE = 7;
const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Meia-noite local do dia da data — mesmo tratamento que calcularProgresso já usa pra
 *  "atrasadas", pra as duas contagens nunca divergirem por causa de hora/fuso. */
const diaDe = (iso: string): number => new Date(`${iso.slice(0, 10)}T00:00:00`).getTime();

export const calcularResumoExecucao = (
  projetos: Pick<Projeto, 'id' | 'nome' | 'etapa'>[],
  tarefas: TarefaResumoExecucao[],
  hoje: Date = new Date(),
): ResumoExecucao => {
  const progresso = calcularProgresso(tarefas, hoje);

  const inicioDoDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
  const inicioDaJanela = inicioDoDia - (DIAS_JANELA_RECENTE - 1) * MS_POR_DIA;
  const concluidasUltimos7Dias = tarefas.filter(t => {
    if (!t.data_conclusao) return false;
    const dia = diaDe(t.data_conclusao);
    return dia >= inicioDaJanela && dia <= inicioDoDia;
  }).length;

  const idsComVinculo = new Set(
    tarefas.map(t => t.projeto_id).filter((id): id is number => id != null),
  );
  const projetosSemTarefa = projetos
    .filter(p => !idsComVinculo.has(p.id))
    .map(p => ({ id: p.id, nome: p.nome }));

  return {
    projetosEmExecucao: projetos.filter(p => p.etapa === 'em_execucao').length,
    tarefasAbertas: progresso.total - progresso.concluidas,
    tarefasAtrasadas: progresso.atrasadas,
    concluidasUltimos7Dias,
    projetosSemTarefa,
  };
};
